import React, { useMemo, useState } from 'react';
import { useWorkplacesCsszQuery } from '../../../../../../graphql/graphql';
import Loading from '../../../../../../Components/Loading';
import MagicForm from '../../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../../Enums/MagicField.enum';
import apiClient from '../../../../../../Services/ApiClient';
import useApiClient from '../../../../../Base/Hooks/useApiClient';
import useNotification from '../../../../../../Hooks/useNotification.hook';
import useTranslation from '../../../../../../Hooks/useTranslation';

type ManagerHookUpCSSZFormValues = Record<
	string,
	{ add: boolean; clientId?: string }
>;

interface Props {
	onSave: () => void;
}

const ManagerHookUpCSSZ: React.FC<Props> = ({ onSave }) => {
	const { displayError } = useApiClient();
	const { showNotificationAlert } = useNotification();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const { data, isLoading: isDataLoading, refetch } = useWorkplacesCsszQuery();

	const formScheme: ItemScheme[] = useMemo(() => {
		const temp: ItemScheme[] = [];
		data?.workplaces?.data?.forEach(workplace => {
			temp.push({
				type: MagicField.CONTAINER,
				key: workplace?.id!,
				children: [
					{
						type: MagicField.CHECKBOX,
						key: 'add',
						title: workplace?.name!,
					},
					{
						type: MagicField.CONDITION,
						settings: {
							rules: [
								{
									formControlKey: `${workplace?.id}.add`,
									rule: 'equal',
									value: true,
								},
							],
						},
						subType: 'view',
						children: [
							{
								type: MagicField.TEXT_FIELD,
								key: 'clientId',
								title: 'component.hookUpWithCSSZComponent.clientId',
								settings: {
									required: true,
								},
							},
						],
					},
				],
			});
		});
		return temp;
	}, [data]);

	const defaultValues: ManagerHookUpCSSZFormValues = useMemo(() => {
		const temp: ManagerHookUpCSSZFormValues = {};

		data?.workplaces?.data?.forEach(workplace => {
			if (workplace) {
				const csszClientId = workplace.meta?.find(
					item => item?.key === 'csszClientId',
				);
				temp[workplace.id] = {
					add: !!csszClientId,
					clientId: csszClientId?.value ?? undefined,
				};
			}
		});

		return temp;
	}, [data]);

	const handleSubmit = async (values: ManagerHookUpCSSZFormValues) => {
		setIsLoading(true);
		const requests = Object.entries(values).map(
			async ([workplaceId, { add, clientId }]) => {
				try {
					if (add) {
						const response = await apiClient.post(
							`/workplaces/${workplaceId}/meta`,
							{
								key: 'csszClientId',
								value: clientId,
							},
						);
						return response.status;
					} else if (defaultValues[workplaceId].clientId) {
						const response = await apiClient.delete(
							`/workplaces/${workplaceId}/meta/csszClientId`,
						);
						return response.status;
					} else {
						return 200;
					}
				} catch (e) {
					displayError(e);
					return 500;
				}
			},
		);

		const statuses = await Promise.all(requests);

		if (statuses.every(status => status === 200)) {
			await refetch();
			onSave();
		} else {
			showNotificationAlert({ content: t('base.shared.warningBug') });
		}
		setIsLoading(false);
	};

	if (isDataLoading || isLoading) {
		return <Loading />;
	}

	return (
		<MagicForm
			formScheme={formScheme}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		/>
	);
};

export default ManagerHookUpCSSZ;
